import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  HostListener,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { APP_NAME } from "../core/constants/app.constants";
import {
  LanguageService,
  SupportedLanguage,
} from "../core/services/language.service";

interface SearchResult {
  title: string;
  description: string;
  url: string;
  type: "Page" | "Guide";
}

interface HeaderCopy {
  home: string;
  blog: string;
  about: string;
  pages: string;
  alternatives: string;
  explore: string;
  language: string;
  startChat: string;
  searchTitle: string;
  searchPlaceholder: string;
  emptySearch: string;
}

const HEADER_CLASSES = {
  pagesMenu: "navbar__menu",
  mobileToggle: "navbar__toggle",
} as const;

const HEADER_COPY: Record<SupportedLanguage, HeaderCopy> = {
  en: {
    home: "Home",
    blog: "Blog",
    about: "About",
    pages: "Pages",
    alternatives: "Alternatives",
    explore: "Explore",
    language: "English",
    startChat: "Start chat",
    searchTitle: `Search ${APP_NAME}`,
    searchPlaceholder: "Search pages and guides...",
    emptySearch: "No matching pages yet.",
  },
  es: {
    home: "Inicio",
    blog: "Blog",
    about: "Acerca de",
    pages: "Páginas",
    alternatives: "Alternativas",
    explore: "Explorar",
    language: "Español",
    startChat: "Iniciar chat",
    searchTitle: `Buscar en ${APP_NAME}`,
    searchPlaceholder: "Buscar páginas y guías...",
    emptySearch: "No se encontraron páginas.",
  },
  de: {
    home: "Startseite",
    blog: "Blog",
    about: "Über uns",
    pages: "Seiten",
    alternatives: "Alternativen",
    explore: "Entdecken",
    language: "Deutsch",
    startChat: "Chat starten",
    searchTitle: `${APP_NAME} durchsuchen`,
    searchPlaceholder: "Seiten und Ratgeber suchen...",
    emptySearch: "Keine passenden Seiten gefunden.",
  },
  fr: {
    home: "Accueil",
    blog: "Blog",
    about: "À propos",
    pages: "Pages",
    alternatives: "Alternatives",
    explore: "Explorer",
    language: "Français",
    startChat: "Démarrer le chat",
    searchTitle: `Rechercher ${APP_NAME}`,
    searchPlaceholder: "Rechercher des pages et guides...",
    emptySearch: "Aucune page trouvée.",
  },
  it: {
    home: "Home",
    blog: "Blog",
    about: "Chi siamo",
    pages: "Pagine",
    alternatives: "Alternative",
    explore: "Esplora",
    language: "Italiano",
    startChat: "Inizia chat",
    searchTitle: `Cerca ${APP_NAME}`,
    searchPlaceholder: "Cerca pagine e guide...",
    emptySearch: "Nessuna pagina trovata.",
  },
  "pt-br": {
    home: "Início",
    blog: "Blog",
    about: "Sobre",
    pages: "Páginas",
    alternatives: "Alternativas",
    explore: "Explorar",
    language: "Português (BR)",
    startChat: "Iniciar chat",
    searchTitle: `Pesquisar ${APP_NAME}`,
    searchPlaceholder: "Pesquisar páginas e guias...",
    emptySearch: "Nenhuma página encontrada.",
  },
  ja: {
    home: "ホーム",
    blog: "ブログ",
    about: `${APP_NAME}について`,
    pages: "ページ",
    alternatives: "代替サービス",
    explore: "探す",
    language: "日本語",
    startChat: "チャットを開始",
    searchTitle: `${APP_NAME}を検索`,
    searchPlaceholder: "ページとガイドを検索...",
    emptySearch: "一致するページがありません。",
  },
};

const SEARCH_INDEX: SearchResult[] = [
  {
    title: "Chat with strangers",
    description: "Find a text-chat match through shared interests.",
    url: "/chat-with-strangers",
    type: "Page",
  },
  {
    title: "Random chat",
    description: "Meet people worldwide through anonymous text chat.",
    url: "/random-chat",
    type: "Page",
  },
  {
    title: "Anonymous chat",
    description: "Chat without creating a public profile.",
    url: "/anonymous-chat",
    type: "Page",
  },
  {
    title: "Make friends online",
    description: "Meet people worldwide in a low-pressure conversation.",
    url: "/make-friends-online",
    type: "Page",
  },
  {
    title: "Omegle alternative",
    description: "A text-first alternative for anonymous conversations.",
    url: "/omegle-alternative",
    type: "Page",
  },
  {
    title: "OmeTV alternative",
    description: "Meet strangers without starting on camera.",
    url: "/ometv-alternative",
    type: "Page",
  },
  {
    title: "Chatroulette alternative",
    description: "A simple text-first random chat alternative.",
    url: "/chatroulette-alternative",
    type: "Page",
  },
  {
    title: "Emerald Chat alternative",
    description: "Interest-aware anonymous text matching.",
    url: "/emerald-chat-alternative",
    type: "Page",
  },
  {
    title: "Monkey App alternative",
    description: "Private one-to-one chat without a public feed.",
    url: "/monkey-app-alternative",
    type: "Page",
  },
  {
    title: `${APP_NAME} Blog`,
    description: "Guides about chat, privacy, and online safety.",
    url: "/blog",
    type: "Guide",
  },
  {
    title: "Safety tips",
    description: "How to stay safe in anonymous online chat.",
    url: "/safety-tips",
    type: "Guide",
  },
  {
    title: `About ${APP_NAME}`,
    description: "Our mission and approach to human conversations.",
    url: "/about",
    type: "Page",
  },
];

@Component({
  selector: "app-header",
  standalone: true,
  imports: [FormsModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./app-header.component.html",
  styleUrl: "./app-header.component.scss",
})
export class AppHeaderComponent {
  readonly appName = APP_NAME;
  readonly headerClasses = HEADER_CLASSES;
  private readonly router = inject(Router);
  readonly searchOpen = signal(false);
  readonly pagesOpen = signal(false);
  readonly languageOpen = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly query = signal("");
  readonly languageService = inject(LanguageService);
  readonly copy = computed(() => HEADER_COPY[this.languageService.language()]);
  readonly results = computed(() => {
    const query = this.query().trim().toLowerCase();
    if (!query) return SEARCH_INDEX;
    return SEARCH_INDEX.filter((result) =>
      `${result.title} ${result.description} ${result.type}`
        .toLowerCase()
        .includes(query),
    );
  });

  openSearch() {
    this.searchOpen.set(true);
    this.query.set("");
  }

  closeSearch() {
    this.searchOpen.set(false);
  }

  togglePages() {
    this.pagesOpen.update((open) => !open);
  }

  closePages() {
    this.pagesOpen.set(false);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update((open) => !open);
    this.pagesOpen.set(false);
    this.languageOpen.set(false);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  toggleLanguage() {
    this.languageOpen.update((open) => !open);
    this.pagesOpen.set(false);
  }

  selectLanguage(language: SupportedLanguage) {
    this.languageService.select(language);
    this.languageOpen.set(false);
    this.closeMobileMenu();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const target = event.target;
    if (
      !(target instanceof HTMLElement) ||
      !target.closest(`.${HEADER_CLASSES.pagesMenu}`)
    ) {
      this.closePages();
    }
    if (!(target instanceof HTMLElement) || !target.closest(".language-menu")) {
      this.languageOpen.set(false);
    }
    if (
      !(target instanceof HTMLElement) ||
      (!target.closest(`.${HEADER_CLASSES.mobileToggle}`) &&
        !target.closest(".mobile-nav"))
    ) {
      this.closeMobileMenu();
    }
  }

  chat() {
    this.closeMobileMenu();
    this.router.navigateByUrl("/chat");
  }
}
